import React, { useState, useEffect } from 'react';
import { MessageSquare, ThumbsUp, User, Clock, ChevronRight, Plus, MessageCircle } from 'lucide-react';
import { forumAPI } from '../services/api';

// Style constants matching ReactorHub theme
const colors = {
  black: '#000000',
  white: '#ffffff',
  gray: {
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
  purple: {
    400: '#A78BFA',
    500: '#8B5CF6',
    600: '#7C3AED',
  },
  orange: {
    500: '#F97316',
    600: '#EA580C',
  },
  blue: {
    400: '#60A5FA',
    500: '#3B82F6',
  }
};

/**
 * ThreadPreview Component
 *
 * Displays thread preview near peak moment cards (or other content)
 * Shows thread title + 2-3 recent comments to induce users to click
 *
 * @param {string} contentType - Type of content (peak_moment, movie, tv_show, channel)
 * @param {string} contentId - ID of the content
 * @param {object} contentData - Content information (title, thumbnail, etc.)
 * @param {function} onStartThread - Callback when "Start Thread" button is clicked
 * @param {function} onViewThread - Callback when preview is clicked to view full thread
 */
const ThreadPreview = ({
  contentType,
  contentId,
  contentData = {},
  onStartThread,
  onViewThread,
  compact = false
}) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPreview();
  }, [contentType, contentId]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await forumAPI.getThreadPreview(contentType, contentId);

      if (response.success && response.preview) {
        setPreview(response.preview);
      } else {
        setPreview(null);
      }
    } catch (err) {
      console.error('Error loading thread preview:', err);
      setError(err.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleThreadClick = () => {
    if (preview?.thread && onViewThread) {
      onViewThread(preview.thread.slug, preview.thread._id);
    }
  };

  const handleStartThread = (e) => {
    e.stopPropagation();
    if (onStartThread) {
      // Map contentType to valid enum values
      const typeMapping = {
        'reactor_video': 'channel',
        'peak_moment': 'peak_moment',
        'movie': 'movie',
        'tv': 'tv_show',
        'tv_show': 'tv_show',
        'channel': 'channel'
      };

      onStartThread({
        type: typeMapping[contentType] || 'general',
        id: contentId,
        title: contentData.title,
        thumbnail: contentData.thumbnail,
        ...contentData
      });
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const truncateText = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Loading state
  if (loading) {
    if (compact) {
      return null;
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        color: colors.gray[500],
        fontSize: '10px'
      }}>
        <MessageSquare size={10} />
      </div>
    );
  }

  // Error state - show nothing
  if (error) {
    return null;
  }

  if (compact) {
    const hasThread = !!preview?.thread;
    const replies = preview?.thread?.stats?.replies || 0;

    return (
      <button
        onClick={hasThread ? handleThreadClick : handleStartThread}
        style={{
          padding: '4px 10px',
          backgroundColor: hasThread ? 'rgba(139, 92, 246, 0.12)' : 'transparent',
          border: `1px solid ${hasThread ? colors.purple[500] : colors.gray[700]}`,
          borderRadius: '6px',
          color: hasThread ? colors.purple[400] : colors.gray[300],
          cursor: 'pointer',
          fontSize: '11px',
          fontWeight: '600',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = hasThread ? 'rgba(139, 92, 246, 0.2)' : colors.gray[800];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = hasThread ? 'rgba(139, 92, 246, 0.12)' : 'transparent';
        }}
        title={hasThread ? 'Open discussion thread' : 'Start discussion thread'}
      >
        {hasThread ? <MessageSquare size={11} /> : <MessageCircle size={11} />}
        {hasThread ? `Thread${replies ? ` ${replies}` : ''}` : 'Thread'}
      </button>
    );
  }

  // No thread exists - show "Start Thread" button
  if (!preview || !preview.thread) {
    return (
      <button
        onClick={handleStartThread}
        style={{
          padding: '4px 8px',
          backgroundColor: colors.purple[600],
          border: 'none',
          borderRadius: '6px',
          color: colors.white,
          cursor: 'pointer',
          fontSize: '10px',
          fontWeight: '600',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '3px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.purple[500];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.purple[600];
        }}
        title="Start Discussion"
      >
        <MessageCircle size={11} />
      </button>
    );
  }

  const { thread, recentPosts = [] } = preview;

  // Thread exists - show preview
  return (
    <div
      onClick={handleThreadClick}
      style={{
        backgroundColor: colors.gray[900],
        borderRadius: '12px',
        padding: '12px',
        border: `1px solid ${colors.gray[800]}`,
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = colors.gray[800];
        e.currentTarget.style.borderColor = colors.purple[500];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = colors.gray[900];
        e.currentTarget.style.borderColor = colors.gray[800];
      }}
    >
      {/* Thread Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '4px'
          }}>
            <MessageSquare size={14} style={{ color: colors.purple[400] }} />
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: colors.white,
              margin: 0,
              lineHeight: '1.3'
            }}>
              {truncateText(thread.title, 60)}
            </h4>
          </div>

          {/* Thread Stats */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '11px',
            color: colors.gray[400]
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <MessageSquare size={10} />
              <span>{thread.stats?.replies || 0}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ThumbsUp size={10} />
              <span>{thread.stats?.likes || 0}</span>
            </div>
            {thread.stats?.lastActivityAt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={10} />
                <span>{formatDate(thread.stats.lastActivityAt)}</span>
              </div>
            )}
          </div>
        </div>

        <ChevronRight size={16} style={{ color: colors.gray[500], flexShrink: 0 }} />
      </div>

      {/* Recent Comments Preview */}
      {recentPosts.length > 0 && (
        <div style={{
          backgroundColor: colors.gray[800] + '50',
          borderRadius: '8px',
          padding: '8px',
          marginTop: '8px'
        }}>
          {recentPosts.slice(0, 2).map((post, index) => (
            <div
              key={post._id || index}
              style={{
                fontSize: '12px',
                color: colors.gray[400],
                marginBottom: index < recentPosts.length - 1 ? '6px' : 0,
                paddingBottom: index < recentPosts.length - 1 ? '6px' : 0,
                borderBottom: index < recentPosts.length - 1 ? `1px solid ${colors.gray[700]}` : 'none'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '4px'
              }}>
                <User size={10} style={{ color: colors.purple[400] }} />
                <span style={{ fontWeight: '500', color: colors.gray[300] }}>
                  {post.authorUsername}
                </span>
                <span style={{ color: colors.gray[500] }}>•</span>
                <span style={{ color: colors.gray[500] }}>
                  {formatDate(post.createdAt)}
                </span>
              </div>
              <p style={{
                margin: 0,
                lineHeight: '1.4',
                color: colors.gray[400]
              }}>
                {truncateText(post.content, 100)}
              </p>
            </div>
          ))}

          {recentPosts.length > 2 && (
            <div style={{
              fontSize: '11px',
              color: colors.purple[400],
              marginTop: '6px',
              fontWeight: '500'
            }}>
              + {recentPosts.length - 2} more comment{recentPosts.length - 2 !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {/* Click hint */}
      <div style={{
        marginTop: '8px',
        fontSize: '11px',
        color: colors.purple[400],
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        View full discussion
        <ChevronRight size={12} />
      </div>
    </div>
  );
};

export default ThreadPreview;
